import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { combineLatest, filter, Observable } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { combineQueries } from '@datorama/akita';
import { getToothNumberById, IntuitiveIndication, IntuitiveToothForDiagram, TeethNumberingSystem } from '../../models';
import { IntuitiveBridge, IntuitiveBridgeValidationErrors } from '../../models/intuitive-bridge';
import { RxIntuitiveTeethHelperService } from '../rx-intuitive-teeth-helper.service';
import { RxIntuitiveTeethImagesService } from '../rx-intuitive-teeth-images.service';
import { RxIntuitiveIndicationMapperService } from '../rx-intuitive-indication-mapper.service';
import { RxIntuitiveIndicationRulesService } from '../rx-intuitive-indication-rules.service';
import { RxIntuitiveTeethDiagramQuery } from '../../state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveTeethDiagramStore } from '../../state/rx-intuitive-teeth-diagram-store';
import { RxIntuitiveBridgeService } from '../rx-intuitive-bridge-service';

/**
 * Owns all view / query observables for the teeth diagram.
 *
 * Both {@link RxIntuitiveTeethDiagramFacade} and
 * {@link RxIntuitiveTeethDiagramBridgeFacade} compose this service so that
 * the same derived observables are not duplicated across the two thin facades.
 */
@Injectable()
export class RxIntuitiveTeethDiagramViewService {
	/**
	 * Selected tooth IDs from the intuitive diagram store.
	 * Exposed here so both facades can delegate without importing the query directly.
	 */
	readonly selectedToothIds$ = this.intuitiveQuery.selectedToothIds$;

	/**
	 * Whether the teeth diagram should be rendered in read-only mode
	 * (shell read-only gate OR the host is an iTero Lab device).
	 */
	readonly isTeethDiagramReadOnly$: Observable<boolean> = combineLatest([
		this.shellQuery.getReadOnlyState('teethDiagram'),
		this.hostPlatformService.isIteroLab$
	]).pipe(map(([isReadOnly, isIteroLab]) => isReadOnly || isIteroLab));

	/**
	 * Complete list of indications available for the current procedure.
	 */
	readonly visibleIndications$: Observable<IntuitiveIndication[]> = combineLatest([
		this.orderQuery.procedureMap$,
		this.shellQuery.rxConfiguration$,
		this.hostPlatformService.isIteroModeling$
	]).pipe(
		map(([procedureMap, rxConfiguration, isIteroModeling]) => {
			const withBridges = (procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.FixedRestorative;

			return this.indicationsService.getProcedureIndications(procedureMap, rxConfiguration, isIteroModeling, withBridges);
		})
	);

	/**
	 * Subset of {@link visibleIndications$} compatible with all currently-selected teeth.
	 */
	readonly enabledIndications$: Observable<IntuitiveIndication[]> = combineLatest([
		this.teethHelper.selectedTeeth$,
		this.teethDiagramQuery.upperJaw$,
		this.teethDiagramQuery.lowerJaw$,
		this.orderQuery.procedureMap$,
		this.shellQuery.rxConfiguration$,
		this.hostPlatformService.isIteroModeling$
	]).pipe(
		map(([selectedTeeth, upperJaw, lowerJaw, procedureMap, rxConfiguration, isIteroModeling]) => {
			const canCreateBridge = !!this.bridgeService.canCreateBridgeFromTeeth(selectedTeeth, { upperJaw, lowerJaw });

			return this.indicationsService.getCommonIndicationsForTeeth(
				selectedTeeth,
				procedureMap,
				rxConfiguration,
				isIteroModeling as boolean,
				canCreateBridge
			);
		})
	);

	/**
	 * Single indication ID when all selected teeth share the same non-null
	 * indication; otherwise [].
	 */
	readonly selectedTeethIndicationIds$: Observable<IntuitiveIndication[]> = this.teethHelper.selectedTeeth$.pipe(
		map((selected: Tooth[]) => {
			if (selected.length === 0) {
				return [];
			}

			const effectiveIds = selected.map((t: Tooth) => this.indicationMapper.getUnitTypeIndicationForDiagram(t.UnitTypeID));
			const firstEffective = effectiveIds[0];

			if (firstEffective == null) {
				return [];
			}

			return effectiveIds.every((id: IntuitiveIndication) => id === firstEffective) ? [firstEffective] : [];
		})
	);

	/**
	 * Toolbar indication list: full procedure list when editable; in diagram
	 * read-only, distinct non-default indications present on any tooth in the
	 * diagram.
	 */
	readonly indicationToolbarIds$: Observable<IntuitiveIndication[]> = combineLatest([
		this.isTeethDiagramReadOnly$,
		this.visibleIndications$,
		this.teethDiagramQuery.teethFlat$
	]).pipe(
		map(([readOnly, visible, teethFlat]) => {
			if (!readOnly) {
				return visible;
			}

			const distinct = new Set<IntuitiveIndication>();

			for (const t of teethFlat ?? []) {
				const id = t.ToothInBridgeTypeID
					? IntuitiveIndication.Bridge
					: this.indicationMapper.getUnitTypeIndicationForDiagram(t.UnitTypeID);

				if (!this.indicationMapper.isDefaultIndication(id)) {
					distinct.add(id);
				}
			}

			return visible.filter(ind => distinct.has(ind));
		}),
		distinctUntilChanged((a, b) => a.length === b.length && a.every((value, index) => value === b[index]))
	);

	/**
	 * Upper jaw teeth mapped to diagram view-models.
	 *
	 * `procedureMap$` is included so models refresh when procedure context
	 * changes even if tooth rows are unchanged (default indication / denture
	 * rules).
	 */
	readonly upperJaw$: Observable<IntuitiveToothForDiagram[]> = combineLatest([
		this.teethDiagramQuery.upperJaw$,
		this.intuitiveQuery.selectedToothIds$,
		this.teethHelper.teethNumberingSystem$,
		this.orderQuery.procedureMap$,
		this.teethHelper.bridges$
	]).pipe(
		map(([jaw, selectedToothIds, teethNumberingSystem, , bridges]) =>
			jaw.map(t => {
				const bridge = bridges && t.BridgeIndex ? bridges[t.BridgeIndex] : undefined;

				return this.toDiagramVM(t, selectedToothIds, teethNumberingSystem, bridge);
			})
		)
	);

	/** See {@link upperJaw$} — same dependency on `procedureMap$`. */
	readonly lowerJaw$: Observable<IntuitiveToothForDiagram[]> = combineLatest([
		this.teethDiagramQuery.lowerJaw$,
		this.intuitiveQuery.selectedToothIds$,
		this.teethHelper.teethNumberingSystem$,
		this.orderQuery.procedureMap$,
		this.teethHelper.bridges$
	]).pipe(
		map(([jaw, selectedToothIds, teethNumberingSystem, , bridges]) =>
			jaw.map(t => {
				const bridge = bridges && t.BridgeIndex ? bridges[t.BridgeIndex] : undefined;

				return this.toDiagramVM(t, selectedToothIds, teethNumberingSystem, bridge);
			})
		)
	);

	/**
	 * Observable that fetches teeth from the shell query and syncs the teeth
	 * diagram store.  Used to update the diagram when the Rx or procedure
	 * changes.
	 */
	readonly fetchRxAndSyncStore$ = combineQueries([this.shellQuery.teeth$, this.shellQuery.isProcedureChanged$]).pipe(
		filter(([, isProcedureChanged]) => !isProcedureChanged),
		map(([teeth]) => teeth),
		tap((teeth: Tooth[]) => {
			this.intuitiveStore.update({ toothLastTouchedAt: {} });
			if (!!teeth) {
				this.teethDiagramStore.resetTeeth();
				this.teethDiagramStore.updateTeeth({ teethToUpdate: teeth });
			}
		})
	);

	constructor(
		private readonly teethHelper: RxIntuitiveTeethHelperService,
		private readonly imagesService: RxIntuitiveTeethImagesService,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService,
		private readonly indicationsService: RxIntuitiveIndicationRulesService,
		private readonly bridgeService: RxIntuitiveBridgeService,
		private readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery,
		private readonly intuitiveStore: RxIntuitiveTeethDiagramStore,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly teethDiagramStore: TeethDiagramStore,
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		private readonly hostPlatformService: HostPlatformService
	) {}

	/**
	 * Maps a single {@link Tooth} (or bridge-tooth) to the view-model consumed
	 * by the teeth-diagram component.
	 *
	 * Made public so the bridge facade can call it when building its overridden
	 * jaw observables — same pattern as `computeCanEditToothEditor` in
	 * {@link RxIntuitiveToothEditorViewService}.
	 */
	toDiagramVM(
		tooth: Tooth,
		selectedToothIds: number[],
		teethNumberingSystem: TeethNumberingSystem,
		bridge?: IntuitiveBridge,
		validationError?: IntuitiveBridgeValidationErrors
	): IntuitiveToothForDiagram {
		const indicationId = this.teethHelper.getToothIndication(tooth, bridge);

		const toothNumberInSystem = getToothNumberById({
			toothId: tooth.ToothID ?? 0,
			teethNumberingSystem
		}) as ReturnType<typeof getToothNumberById> | undefined;

		return {
			toothId: tooth.ToothID ?? 0,
			toothNumberInSystem: toothNumberInSystem ?? 0,
			indicationId,
			svgSymbolId: this.imagesService.getSymbolId(tooth.ToothID ?? 0, indicationId),
			isSelected: selectedToothIds.includes(tooth.ToothID ?? 0),
			bridgeId: bridge?.index,
			bridge: bridge
				? {
						index: bridge.index,
						isOuterLeft: bridge.outerLeftToothId === tooth.ToothID,
						isOuterRight: bridge.outerRightToothId === tooth.ToothID,
						isSelected: this.intuitiveQuery.selectedBridge?.index === bridge.index
					}
				: undefined,
			isInvalid: tooth.ToothID != null && !!validationError?.teeth?.[tooth.ToothID]?.length,
			isInvalidForSend: this.teethHelper.validateToothForSend(tooth, bridge)
		};
	}
}
